import {
  CameraPosition,
  InteractionPointPosition,
  PropPosition,
  RgbaColor,
  TakePosition,
} from "../../app/slices/EnvironmentSlice";
import { SituationMetadata } from "../../dto/SituationMetadata";

export type EnvironmentPositionContext = {
  takePositionId: string;
  takePositionName: string;
  takeLocationId: string;
  situationId?: string;
  takeColor?: RgbaColor;
  actorPositions: InteractionPointPosition[];
  propPositions: PropPosition[];
  cameraPositions: CameraPosition[];
};

export type EnvironmentPositionOption = {
  id: string;
  sourceId: string;
  name: string;
  displayName: string;
  context: EnvironmentPositionContext;
};

export function getEnvironmentPositionContexts(
  takePositions: TakePosition[],
  situations: Record<string, string> | undefined,
  getSituationMetadata: (situationId: string) => SituationMetadata,
): EnvironmentPositionContext[] {
  return takePositions.map((takePosition) => {
    const situationId = situations?.[takePosition.id];
    const metadata = situationId
      ? getSituationMetadata(situationId)
      : takePosition;

    return {
      takePositionId: takePosition.id,
      takePositionName: takePosition.name,
      takeLocationId: situationId ?? takePosition.id,
      situationId,
      takeColor: takePosition.take_color,
      actorPositions: metadata.actor_positions ?? [],
      propPositions: metadata.prop_positions ?? [],
      cameraPositions: metadata.camera_positions ?? [],
    };
  });
}

export function getSituationPositionId(
  context: EnvironmentPositionContext,
  positionId: string,
): string {
  return context.situationId
    ? `${context.situationId}/${positionId}`
    : positionId;
}

export function addEnvironmentPositionDisplayNames(
  options: Omit<EnvironmentPositionOption, "displayName">[],
): EnvironmentPositionOption[] {
  const nameCounts = new Map<string, number>();
  options.forEach((option) => {
    nameCounts.set(option.name, (nameCounts.get(option.name) ?? 0) + 1);
  });

  return options.map((option) => ({
    ...option,
    displayName:
      (nameCounts.get(option.name) ?? 0) > 1
        ? `${option.name} (${option.context.takePositionName || option.context.takePositionId})`
        : option.name,
  }));
}
